import { z } from 'zod';

export type FeatureVersionDef<TSchema extends z.ZodType = z.ZodType> = {
    schema: TSchema;
    /** Migrate from the previous version's shape. Absent for the initial version. */
    up?: (old: any) => unknown;
};

type FeatureVersionMap = Record<number, FeatureVersionDef>;

/**
 * Creates a self-versioned feature: a reusable, cross-entity schema fragment whose data
 * carries its own `version` field and migrates itself at parse time. Entities embed
 * `feature.fieldSchema` (or `feature.schema`) and never bump their own version when the
 * feature evolves. Data without a `version` field predates self-versioning and is treated as v0.
 */
export const createVersionedFeature = <M extends FeatureVersionMap, L extends keyof M & number>(input: {
    versionMap: M;
    latestVersion: L;
    createDefault: () => z.output<M[L]['schema']>;
}) => {
    const { versionMap, latestVersion, createDefault } = input;
    const latestSchema = versionMap[latestVersion].schema as M[L]['schema'];

    const getVersion = (data: unknown): number => {
        if (typeof data !== 'object' || data === null) return 0;
        const version = (data as { version?: unknown }).version;
        return typeof version === 'number' ? version : 0;
    };

    const migrate = (data: unknown): unknown => {
        if (typeof data !== 'object' || data === null) return data;

        let version = getVersion(data);
        let current: unknown = 'version' in data ? data : { ...data, version: 0 };

        while (version < latestVersion) {
            const next = versionMap[version + 1];
            if (!next?.up) return current; // let latestSchema surface the error
            current = next.up(current);
            version += 1;
        }
        return current;
    };

    const schema = z.preprocess(migrate, latestSchema);
    // cast: zod cannot prove the generic schema output is non-undefined
    const fieldSchema = schema.optional().default(createDefault as () => any);

    return {
        /** Self-migrating schema for required embedding. */
        schema,
        /** Ready-to-embed schema: defaults when the field is absent in persisted data. */
        fieldSchema,
        latestSchema,
        latestVersion,
        versionMap,
        createDefault,
        migrate,
        getVersion,
    };
};
